/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { motion } from 'framer-motion';
import {
    Crown, Compass, Heart, Activity, AlertTriangle, Sparkles,
    Target, Calendar, TrendingUp, MessageCircle, Bell, Brain, Zap, ArrowRight,
} from 'lucide-react';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };
const MONO = { fontFamily: "'JetBrains Mono', monospace" };

/**
 * ConvCopilots — Différenciateur clé : 3 IA spécialisées (une par rôle).
 * Carte d'identité riche par copilote : Pilote (dirigeant), Coach (négociateur),
 * Confident (vendeur).
 */
export const ConvCopilots = () => {
    return (
        <section className="relative py-16 md:py-24" data-testid="copilots-section">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.6 }}
                    className="text-center"
                >
                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#681793]/30 bg-[#681793]/10 text-[11px] font-medium tracking-widest text-[#C084FC] uppercase" style={MONO}>
                        4 IA spécialisées · 1 vision unifiée
                    </span>
                    <h2 className="mt-6 text-3xl sm:text-4xl font-bold tracking-tighter text-white max-w-3xl mx-auto" style={DISPLAY}>
                        Quatre IA spécialisées.{' '}
                        <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">Une pour chaque rôle.</span>
                    </h2>
                    <p className="mt-4 text-sm text-white/55 max-w-xl mx-auto">
                        Trois IA pour piloter votre agence au quotidien. Une quatrième pour les dirigeants multi-agences.
                    </p>
                </motion.div>

                <div className="mt-12 grid md:grid-cols-3 gap-5 md:gap-6" data-testid="three-copilots-grid">
                    {/* PILOTE — Dirigeant */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0 }}
                        className="group relative"
                        data-testid="copilot-pilote"
                    >
                        <div className="relative bg-gradient-to-br from-[#1a0f2e] via-[#12121a] to-[#0f0a1e] rounded-2xl p-6 md:p-7 border border-[#A855F7]/20 hover:border-[#A855F7]/50 transition-all h-full overflow-hidden">
                            <div className="absolute -top-16 -right-16 w-40 h-40 bg-[#A855F7]/20 rounded-full blur-[80px] pointer-events-none" />
                            <div className="relative z-10">
                                <div className="flex items-center justify-between mb-5">
                                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#A855F7] to-[#681793] flex items-center justify-center shadow-lg shadow-[#A855F7]/30">
                                        <Crown className="w-7 h-7 text-white" />
                                    </div>
                                    <span className="px-2.5 py-1 text-[10px] font-bold tracking-widest uppercase bg-[#A855F7]/15 text-[#C084FC] rounded-md border border-[#A855F7]/30">
                                        Pour le dirigeant
                                    </span>
                                </div>
                                <h3 className="text-2xl font-black text-white mb-1.5" style={DISPLAY}>Pilote</h3>
                                <p className="text-xs text-[#C084FC] font-mono uppercase tracking-wider mb-3">Le copilote du dirigeant</p>
                                <p className="text-sm text-white/65 mb-5 leading-relaxed">
                                    Tient le pouls de l'agence en continu. <strong className="text-white">Repère les signes qu'on ne voit pas à l'œil nu</strong>, prévient les mandats qui partent, et vous donne <strong className="text-white">une décision concrète chaque matin</strong>.
                                </p>
                                <ul className="space-y-2 text-[13px] text-white/75">
                                    <li className="flex items-start gap-2">
                                        <Activity className="w-3.5 h-3.5 text-[#C084FC] shrink-0 mt-0.5" />
                                        <span>7 signaux faibles surveillés (silences, agents en surcharge, vagues d'inquiétude…)</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <AlertTriangle className="w-3.5 h-3.5 text-[#C084FC] shrink-0 mt-0.5" />
                                        <span>Score de risque par mandat — vous savez lequel relancer en priorité</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <Sparkles className="w-3.5 h-3.5 text-[#C084FC] shrink-0 mt-0.5" />
                                        <span>Briefing du jour + synthèse du lundi — directement par email</span>
                                    </li>
                                </ul>
                                <div className="mt-5 pt-4 border-t border-white/5 flex items-center gap-2 text-[10px] text-white/40 font-mono uppercase tracking-wider">
                                    <Brain className="w-3 h-3 text-[#C084FC]" />
                                    Moteur IA Homly
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* COACH — Négociateur */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="group relative"
                        data-testid="copilot-coach"
                    >
                        <div className="relative bg-gradient-to-br from-[#2e1a0f] via-[#1a1410] to-[#1f0e0a] rounded-2xl p-6 md:p-7 border border-[#FF914D]/20 hover:border-[#FF914D]/50 transition-all h-full overflow-hidden">
                            <div className="absolute -top-16 -right-16 w-40 h-40 bg-[#FF914D]/20 rounded-full blur-[80px] pointer-events-none" />
                            <div className="relative z-10">
                                <div className="flex items-center justify-between mb-5">
                                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF914D] to-[#e67a2f] flex items-center justify-center shadow-lg shadow-[#FF914D]/30">
                                        <Compass className="w-7 h-7 text-white" />
                                    </div>
                                    <span className="px-2.5 py-1 text-[10px] font-bold tracking-widest uppercase bg-[#FF914D]/15 text-[#FFB07A] rounded-md border border-[#FF914D]/30">
                                        Pour le négociateur
                                    </span>
                                </div>
                                <h3 className="text-2xl font-black text-white mb-1.5" style={DISPLAY}>Coach</h3>
                                <p className="text-xs text-[#FFB07A] font-mono uppercase tracking-wider mb-3">Le coach personnel</p>
                                <p className="text-sm text-white/65 mb-5 leading-relaxed">
                                    Suit chaque négociateur <strong className="text-white">individuellement</strong>. Prépare ses rendez-vous vendeur en 10 secondes (points-clés + script). Identifie ses 2 forces et ses 2 axes d'amélioration chaque semaine.
                                </p>
                                <ul className="space-y-2 text-[13px] text-white/75">
                                    <li className="flex items-start gap-2">
                                        <Target className="w-3.5 h-3.5 text-[#FFB07A] shrink-0 mt-0.5" />
                                        <span>Coaching hebdo sur 6 axes (prospection, signature, conclusion de vente, satisfaction…)</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <Calendar className="w-3.5 h-3.5 text-[#FFB07A] shrink-0 mt-0.5" />
                                        <span>Préparation de rendez-vous vendeur : 3 points-clés + script prêt à l'emploi</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <TrendingUp className="w-3.5 h-3.5 text-[#FFB07A] shrink-0 mt-0.5" />
                                        <span>Priorités du jour triées par impact en € et urgence</span>
                                    </li>
                                </ul>
                                <div className="mt-5 pt-4 border-t border-white/5 flex items-center gap-2 text-[10px] text-white/40 font-mono uppercase tracking-wider">
                                    <Brain className="w-3 h-3 text-[#FFB07A]" />
                                    Moteur IA Homly
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* CONFIDENT — Vendeur */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="group relative"
                        data-testid="copilot-confident"
                    >
                        <div className="relative bg-gradient-to-br from-[#0a1f1a] via-[#101a18] to-[#0e1a20] rounded-2xl p-6 md:p-7 border border-[#10B981]/20 hover:border-[#10B981]/50 transition-all h-full overflow-hidden">
                            <div className="absolute -top-16 -right-16 w-40 h-40 bg-[#10B981]/20 rounded-full blur-[80px] pointer-events-none" />
                            <div className="relative z-10">
                                <div className="flex items-center justify-between mb-5">
                                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center shadow-lg shadow-[#10B981]/30">
                                        <Heart className="w-7 h-7 text-white" />
                                    </div>
                                    <span className="px-2.5 py-1 text-[10px] font-bold tracking-widest uppercase bg-[#10B981]/15 text-[#34D399] rounded-md border border-[#10B981]/30">
                                        Pour le vendeur
                                    </span>
                                </div>
                                <h3 className="text-2xl font-black text-white mb-1.5" style={DISPLAY}>Confident</h3>
                                <p className="text-xs text-[#34D399] font-mono uppercase tracking-wider mb-3">L'IA de réassurance</p>
                                <p className="text-sm text-white/65 mb-5 leading-relaxed">
                                    Disponible 24h/24 dans l'espace vendeur. Répond aux questions sensibles (prix, délai, sortie de mandat), <strong className="text-white">détecte l'inquiétude dans les messages</strong>, et alerte le manager <strong className="text-white">avant que le vendeur ne parte voir ailleurs</strong>.
                                </p>
                                <ul className="space-y-2 text-[13px] text-white/75">
                                    <li className="flex items-start gap-2">
                                        <MessageCircle className="w-3.5 h-3.5 text-[#34D399] shrink-0 mt-0.5" />
                                        <span>Questions/réponses 24h/24 sur le mandat (ton apaisé, vouvoiement)</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <Activity className="w-3.5 h-3.5 text-[#34D399] shrink-0 mt-0.5" />
                                        <span>Analyse du sentiment et de l'intention du vendeur en temps réel</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <Bell className="w-3.5 h-3.5 text-[#34D399] shrink-0 mt-0.5" />
                                        <span>Alerte immédiate au manager si le vendeur montre un signe de départ</span>
                                    </li>
                                </ul>
                                <div className="mt-5 pt-4 border-t border-white/5 flex items-center gap-2 text-[10px] text-white/40 font-mono uppercase tracking-wider">
                                    <Zap className="w-3 h-3 text-[#34D399]" />
                                    Moteur IA Homly
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* ============ BANDE HORIZONTALE PROVIDENCE ============ */}
                {/* 4ème IA dédiée au pilotage multi-agence — pont narratif */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="mt-8 relative group cursor-pointer"
                    onClick={() => {
                        const el = document.querySelector('[data-testid="multi-agence-providence-section"]');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }}
                    data-testid="providence-band"
                >
                    <div className="relative bg-gradient-to-r from-[#1a0f2e] via-[#0f0a1e] to-[#1a0a2e] rounded-2xl p-6 md:p-7 border border-[#A855F7]/25 hover:border-[#A855F7]/50 transition-all overflow-hidden">
                        {/* halo */}
                        <div className="absolute -top-20 -left-20 w-72 h-72 bg-[#A855F7]/15 rounded-full blur-[100px] pointer-events-none" />
                        <div className="absolute -bottom-20 -right-20 w-72 h-72 bg-[#FF914D]/10 rounded-full blur-[100px] pointer-events-none" />

                        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center gap-5 md:gap-7">
                            {/* Icon + badge */}
                            <div className="flex items-center gap-4 md:shrink-0">
                                <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-[#A855F7] via-[#C084FC] to-[#FF914D] flex items-center justify-center shadow-lg shadow-[#A855F7]/30">
                                    <Sparkles className="w-7 h-7 text-white" />
                                </div>
                                <div className="md:hidden">
                                    <span className="px-2.5 py-1 text-[10px] font-bold tracking-widest uppercase bg-[#A855F7]/15 text-[#C084FC] rounded-md border border-[#A855F7]/30">
                                        + Pour les groupes
                                    </span>
                                </div>
                            </div>

                            {/* Texte */}
                            <div className="flex-1 min-w-0">
                                <div className="hidden md:flex items-center gap-3 mb-2">
                                    <span className="px-2.5 py-1 text-[10px] font-bold tracking-widest uppercase bg-[#A855F7]/15 text-[#C084FC] rounded-md border border-[#A855F7]/30">
                                        + Pour les groupes
                                    </span>
                                    <span className="text-[10px] uppercase tracking-widest text-white/30 font-mono">La 4ème IA · à l'échelle du réseau</span>
                                </div>
                                <h3 className="text-2xl md:text-3xl font-black text-white mb-1.5 leading-tight" style={DISPLAY}>
                                    Providence — <span className="bg-gradient-to-r from-[#A855F7] via-[#C084FC] to-[#FF914D] bg-clip-text text-transparent">l'IA du dirigeant multi-agence.</span>
                                </h3>
                                <p className="text-sm text-white/65 leading-relaxed">
                                    Pilotez 2 à 50 agences depuis un seul écran. Providence compare, alerte, et prépare vos points avec les directeurs d'agence.
                                </p>
                            </div>

                            {/* CTA */}
                            <div className="flex items-center gap-2 md:shrink-0 text-sm font-semibold text-[#C084FC] group-hover:text-white transition-colors">
                                Découvrir
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};
